/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * Mission Impact Auth API
 * OpenAPI spec version: v1
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  AvailableAddOnsResponse,
  CheckoutResponse,
  ConsultantHourPackageOffersResponse,
  CreateCheckoutRequest,
  CreateConsultantHoursCheckoutRequest,
  CreatePortalRequest,
  GetBillingResponse,
  GetOrgInvoicesParams,
  GetSubscriptionResponse,
  InvoicesListResponse,
  PortalResponse,
  PreviewSubscriptionChangeRequest,
  PreviewSubscriptionChangeResponse,
  ProblemDetails,
  ProductPlansResponse,
} from '../missionImpactAuthAPI.schemas';

import { instance } from '../../mutator-auth';
import type { ErrorType } from '../../mutator-auth';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Creates a Stripe Checkout session for the organization to purchase a subscription. Requires Administrator or Billing role.
 * @summary Create organization checkout session
 */
export const createOrgCheckoutSession = (
  orgId: string,
  createCheckoutRequest: CreateCheckoutRequest,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<CheckoutResponse>(
    {
      url: `/v1/org/${orgId}/billing/checkout`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createCheckoutRequest,
      signal,
    },
    options
  );
};

export const getCreateOrgCheckoutSessionMutationOptions = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOrgCheckoutSession>>,
    TError,
    { orgId: string; data: CreateCheckoutRequest },
    TContext
  >;
  request?: SecondParameter<typeof instance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createOrgCheckoutSession>>,
  TError,
  { orgId: string; data: CreateCheckoutRequest },
  TContext
> => {
  const mutationKey = ['createOrgCheckoutSession'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createOrgCheckoutSession>>,
    { orgId: string; data: CreateCheckoutRequest }
  > = (props) => {
    const { orgId, data } = props ?? {};

    return createOrgCheckoutSession(orgId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateOrgCheckoutSessionMutationResult = NonNullable<
  Awaited<ReturnType<typeof createOrgCheckoutSession>>
>;
export type CreateOrgCheckoutSessionMutationBody = CreateCheckoutRequest;
export type CreateOrgCheckoutSessionMutationError = ErrorType<ProblemDetails>;

/**
 * @summary Create organization checkout session
 */
export const useCreateOrgCheckoutSession = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createOrgCheckoutSession>>,
      TError,
      { orgId: string; data: CreateCheckoutRequest },
      TContext
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createOrgCheckoutSession>>,
  TError,
  { orgId: string; data: CreateCheckoutRequest },
  TContext
> => {
  const mutationOptions = getCreateOrgCheckoutSessionMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Creates a Stripe Billing Portal session for the organization to manage subscriptions. Requires Administrator or Billing role.
 * @summary Create organization billing portal session
 */
export const createOrgPortalSession = (
  orgId: string,
  createPortalRequest: CreatePortalRequest,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<PortalResponse>(
    {
      url: `/v1/org/${orgId}/billing/portal`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createPortalRequest,
      signal,
    },
    options
  );
};

export const getCreateOrgPortalSessionMutationOptions = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOrgPortalSession>>,
    TError,
    { orgId: string; data: CreatePortalRequest },
    TContext
  >;
  request?: SecondParameter<typeof instance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createOrgPortalSession>>,
  TError,
  { orgId: string; data: CreatePortalRequest },
  TContext
> => {
  const mutationKey = ['createOrgPortalSession'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createOrgPortalSession>>,
    { orgId: string; data: CreatePortalRequest }
  > = (props) => {
    const { orgId, data } = props ?? {};

    return createOrgPortalSession(orgId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateOrgPortalSessionMutationResult = NonNullable<
  Awaited<ReturnType<typeof createOrgPortalSession>>
>;
export type CreateOrgPortalSessionMutationBody = CreatePortalRequest;
export type CreateOrgPortalSessionMutationError = ErrorType<ProblemDetails>;

/**
 * @summary Create organization billing portal session
 */
export const useCreateOrgPortalSession = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createOrgPortalSession>>,
      TError,
      { orgId: string; data: CreatePortalRequest },
      TContext
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createOrgPortalSession>>,
  TError,
  { orgId: string; data: CreatePortalRequest },
  TContext
> => {
  const mutationOptions = getCreateOrgPortalSessionMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Gets billing information for the organization including address and default payment method. Requires Administrator or Billing role.
 * @summary Get organization billing information
 */
export const getOrgBilling = (
  orgId: string,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<GetBillingResponse>(
    { url: `/v1/org/${orgId}/billing`, method: 'GET', signal },
    options
  );
};

export const getGetOrgBillingQueryKey = (orgId?: string) => {
  return [`/v1/org/${orgId}/billing`] as const;
};

export const getGetOrgBillingQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrgBilling>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrgBilling>>, TError, TData>>;
    request?: SecondParameter<typeof instance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOrgBillingQueryKey(orgId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrgBilling>>> = ({ signal }) =>
    getOrgBilling(orgId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!orgId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrgBilling>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetOrgBillingQueryResult = NonNullable<Awaited<ReturnType<typeof getOrgBilling>>>;
export type GetOrgBillingQueryError = ErrorType<ProblemDetails>;

export function useGetOrgBilling<
  TData = Awaited<ReturnType<typeof getOrgBilling>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrgBilling>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrgBilling>>,
          TError,
          Awaited<ReturnType<typeof getOrgBilling>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrgBilling<
  TData = Awaited<ReturnType<typeof getOrgBilling>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrgBilling>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrgBilling>>,
          TError,
          Awaited<ReturnType<typeof getOrgBilling>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrgBilling<
  TData = Awaited<ReturnType<typeof getOrgBilling>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrgBilling>>, TError, TData>>;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get organization billing information
 */

export function useGetOrgBilling<
  TData = Awaited<ReturnType<typeof getOrgBilling>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrgBilling>>, TError, TData>>;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetOrgBillingQueryOptions(orgId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Gets paginated invoices for the organization ordered by creation date descending. Requires Administrator or Billing role.
 * @summary Get organization invoices
 */
export const getOrgInvoices = (
  orgId: string,
  params?: GetOrgInvoicesParams,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<InvoicesListResponse>(
    { url: `/v1/org/${orgId}/billing/invoices`, method: 'GET', params, signal },
    options
  );
};

export const getGetOrgInvoicesQueryKey = (orgId?: string, params?: GetOrgInvoicesParams) => {
  return [`/v1/org/${orgId}/billing/invoices`, ...(params ? [params] : [])] as const;
};

export const getGetOrgInvoicesQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrgInvoices>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  params?: GetOrgInvoicesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrgInvoices>>, TError, TData>>;
    request?: SecondParameter<typeof instance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOrgInvoicesQueryKey(orgId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrgInvoices>>> = ({ signal }) =>
    getOrgInvoices(orgId, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!orgId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrgInvoices>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetOrgInvoicesQueryResult = NonNullable<Awaited<ReturnType<typeof getOrgInvoices>>>;
export type GetOrgInvoicesQueryError = ErrorType<ProblemDetails>;

export function useGetOrgInvoices<
  TData = Awaited<ReturnType<typeof getOrgInvoices>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  params: undefined | GetOrgInvoicesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrgInvoices>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrgInvoices>>,
          TError,
          Awaited<ReturnType<typeof getOrgInvoices>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrgInvoices<
  TData = Awaited<ReturnType<typeof getOrgInvoices>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  params?: GetOrgInvoicesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrgInvoices>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrgInvoices>>,
          TError,
          Awaited<ReturnType<typeof getOrgInvoices>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrgInvoices<
  TData = Awaited<ReturnType<typeof getOrgInvoices>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  params?: GetOrgInvoicesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrgInvoices>>, TError, TData>>;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get organization invoices
 */

export function useGetOrgInvoices<
  TData = Awaited<ReturnType<typeof getOrgInvoices>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  params?: GetOrgInvoicesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrgInvoices>>, TError, TData>>;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetOrgInvoicesQueryOptions(orgId, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Gets the plans the organization is eligible to purchase for the specified product, with pricing from the local Stripe mirror. Requires Administrator or Billing role.
 * @summary Get organization product plans
 */
export const getOrgProductPlans = (
  orgId: string,
  productId: string,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<ProductPlansResponse>(
    { url: `/v1/org/${orgId}/billing/products/${productId}/plans`, method: 'GET', signal },
    options
  );
};

export const getGetOrgProductPlansQueryKey = (orgId?: string, productId?: string) => {
  return [`/v1/org/${orgId}/billing/products/${productId}/plans`] as const;
};

export const getGetOrgProductPlansQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrgProductPlans>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrgProductPlans>>, TError, TData>>;
    request?: SecondParameter<typeof instance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOrgProductPlansQueryKey(orgId, productId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrgProductPlans>>> = ({ signal }) =>
    getOrgProductPlans(orgId, productId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!(orgId && productId), ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrgProductPlans>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetOrgProductPlansQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrgProductPlans>>
>;
export type GetOrgProductPlansQueryError = ErrorType<ProblemDetails>;

export function useGetOrgProductPlans<
  TData = Awaited<ReturnType<typeof getOrgProductPlans>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrgProductPlans>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrgProductPlans>>,
          TError,
          Awaited<ReturnType<typeof getOrgProductPlans>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrgProductPlans<
  TData = Awaited<ReturnType<typeof getOrgProductPlans>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgProductPlans>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrgProductPlans>>,
          TError,
          Awaited<ReturnType<typeof getOrgProductPlans>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrgProductPlans<
  TData = Awaited<ReturnType<typeof getOrgProductPlans>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrgProductPlans>>, TError, TData>>;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get organization product plans
 */

export function useGetOrgProductPlans<
  TData = Awaited<ReturnType<typeof getOrgProductPlans>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrgProductPlans>>, TError, TData>>;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetOrgProductPlansQueryOptions(orgId, productId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Gets the subscription containing a line item for the specified product. Requires Administrator or Billing role.
 * @summary Get organization subscription for product
 */
export const getOrgProductSubscription = (
  orgId: string,
  productId: string,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<GetSubscriptionResponse>(
    { url: `/v1/org/${orgId}/billing/products/${productId}/subscription`, method: 'GET', signal },
    options
  );
};

export const getGetOrgProductSubscriptionQueryKey = (orgId?: string, productId?: string) => {
  return [`/v1/org/${orgId}/billing/products/${productId}/subscription`] as const;
};

export const getGetOrgProductSubscriptionQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrgProductSubscription>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgProductSubscription>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOrgProductSubscriptionQueryKey(orgId, productId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrgProductSubscription>>> = ({
    signal,
  }) => getOrgProductSubscription(orgId, productId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!(orgId && productId), ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrgProductSubscription>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetOrgProductSubscriptionQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrgProductSubscription>>
>;
export type GetOrgProductSubscriptionQueryError = ErrorType<ProblemDetails>;

export function useGetOrgProductSubscription<
  TData = Awaited<ReturnType<typeof getOrgProductSubscription>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgProductSubscription>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrgProductSubscription>>,
          TError,
          Awaited<ReturnType<typeof getOrgProductSubscription>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrgProductSubscription<
  TData = Awaited<ReturnType<typeof getOrgProductSubscription>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgProductSubscription>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrgProductSubscription>>,
          TError,
          Awaited<ReturnType<typeof getOrgProductSubscription>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrgProductSubscription<
  TData = Awaited<ReturnType<typeof getOrgProductSubscription>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgProductSubscription>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get organization subscription for product
 */

export function useGetOrgProductSubscription<
  TData = Awaited<ReturnType<typeof getOrgProductSubscription>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgProductSubscription>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetOrgProductSubscriptionQueryOptions(orgId, productId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Gets add-ons available for purchase for the specified subscription. Requires Administrator or Billing role.
 * @summary Get available add-ons for subscription
 */
export const getOrgSubscriptionAvailableAddOns = (
  orgId: string,
  subscriptionId: string,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<AvailableAddOnsResponse>(
    {
      url: `/v1/org/${orgId}/billing/subscriptions/${subscriptionId}/available-addons`,
      method: 'GET',
      signal,
    },
    options
  );
};

export const getGetOrgSubscriptionAvailableAddOnsQueryKey = (
  orgId?: string,
  subscriptionId?: string
) => {
  return [`/v1/org/${orgId}/billing/subscriptions/${subscriptionId}/available-addons`] as const;
};

export const getGetOrgSubscriptionAvailableAddOnsQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  subscriptionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOrgSubscriptionAvailableAddOnsQueryKey(orgId, subscriptionId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>> = ({
    signal,
  }) => getOrgSubscriptionAvailableAddOns(orgId, subscriptionId, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(orgId && subscriptionId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetOrgSubscriptionAvailableAddOnsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>
>;
export type GetOrgSubscriptionAvailableAddOnsQueryError = ErrorType<ProblemDetails>;

export function useGetOrgSubscriptionAvailableAddOns<
  TData = Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  subscriptionId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>,
          TError,
          Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrgSubscriptionAvailableAddOns<
  TData = Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  subscriptionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>,
          TError,
          Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrgSubscriptionAvailableAddOns<
  TData = Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  subscriptionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get available add-ons for subscription
 */

export function useGetOrgSubscriptionAvailableAddOns<
  TData = Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  subscriptionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgSubscriptionAvailableAddOns>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetOrgSubscriptionAvailableAddOnsQueryOptions(
    orgId,
    subscriptionId,
    options
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Previews price changes for proposed subscription modifications. Requires Administrator or Billing role.
 * @summary Preview subscription changes
 */
export const previewOrgSubscriptionChange = (
  orgId: string,
  subscriptionId: string,
  previewSubscriptionChangeRequest: PreviewSubscriptionChangeRequest,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<PreviewSubscriptionChangeResponse>(
    {
      url: `/v1/org/${orgId}/billing/subscriptions/${subscriptionId}/preview`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: previewSubscriptionChangeRequest,
      signal,
    },
    options
  );
};

export const getPreviewOrgSubscriptionChangeMutationOptions = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof previewOrgSubscriptionChange>>,
    TError,
    { orgId: string; subscriptionId: string; data: PreviewSubscriptionChangeRequest },
    TContext
  >;
  request?: SecondParameter<typeof instance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof previewOrgSubscriptionChange>>,
  TError,
  { orgId: string; subscriptionId: string; data: PreviewSubscriptionChangeRequest },
  TContext
> => {
  const mutationKey = ['previewOrgSubscriptionChange'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof previewOrgSubscriptionChange>>,
    { orgId: string; subscriptionId: string; data: PreviewSubscriptionChangeRequest }
  > = (props) => {
    const { orgId, subscriptionId, data } = props ?? {};

    return previewOrgSubscriptionChange(orgId, subscriptionId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PreviewOrgSubscriptionChangeMutationResult = NonNullable<
  Awaited<ReturnType<typeof previewOrgSubscriptionChange>>
>;
export type PreviewOrgSubscriptionChangeMutationBody = PreviewSubscriptionChangeRequest;
export type PreviewOrgSubscriptionChangeMutationError = ErrorType<ProblemDetails>;

/**
 * @summary Preview subscription changes
 */
export const usePreviewOrgSubscriptionChange = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof previewOrgSubscriptionChange>>,
      TError,
      { orgId: string; subscriptionId: string; data: PreviewSubscriptionChangeRequest },
      TContext
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof previewOrgSubscriptionChange>>,
  TError,
  { orgId: string; subscriptionId: string; data: PreviewSubscriptionChangeRequest },
  TContext
> => {
  const mutationOptions = getPreviewOrgSubscriptionChangeMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Applies subscription changes atomically. Upgrades (new items, quantity increases) are charged immediately. Downgrades (removed items, quantity decreases) are scheduled for the next billing period. Requires Administrator or Billing role.
 * @summary Apply subscription changes
 */
export const applyOrgSubscriptionChanges = (
  orgId: string,
  subscriptionId: string,
  previewSubscriptionChangeRequest: PreviewSubscriptionChangeRequest,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<GetSubscriptionResponse>(
    {
      url: `/v1/org/${orgId}/billing/subscriptions/${subscriptionId}/apply`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: previewSubscriptionChangeRequest,
      signal,
    },
    options
  );
};

export const getApplyOrgSubscriptionChangesMutationOptions = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof applyOrgSubscriptionChanges>>,
    TError,
    { orgId: string; subscriptionId: string; data: PreviewSubscriptionChangeRequest },
    TContext
  >;
  request?: SecondParameter<typeof instance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof applyOrgSubscriptionChanges>>,
  TError,
  { orgId: string; subscriptionId: string; data: PreviewSubscriptionChangeRequest },
  TContext
> => {
  const mutationKey = ['applyOrgSubscriptionChanges'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof applyOrgSubscriptionChanges>>,
    { orgId: string; subscriptionId: string; data: PreviewSubscriptionChangeRequest }
  > = (props) => {
    const { orgId, subscriptionId, data } = props ?? {};

    return applyOrgSubscriptionChanges(orgId, subscriptionId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ApplyOrgSubscriptionChangesMutationResult = NonNullable<
  Awaited<ReturnType<typeof applyOrgSubscriptionChanges>>
>;
export type ApplyOrgSubscriptionChangesMutationBody = PreviewSubscriptionChangeRequest;
export type ApplyOrgSubscriptionChangesMutationError = ErrorType<ProblemDetails>;

/**
 * @summary Apply subscription changes
 */
export const useApplyOrgSubscriptionChanges = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof applyOrgSubscriptionChanges>>,
      TError,
      { orgId: string; subscriptionId: string; data: PreviewSubscriptionChangeRequest },
      TContext
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof applyOrgSubscriptionChanges>>,
  TError,
  { orgId: string; subscriptionId: string; data: PreviewSubscriptionChangeRequest },
  TContext
> => {
  const mutationOptions = getApplyOrgSubscriptionChangesMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Creates a Stripe Checkout session for the authenticated user to purchase a subscription.
 * @summary Create checkout session
 */
export const createUserCheckoutSession = (
  createCheckoutRequest: CreateCheckoutRequest,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<CheckoutResponse>(
    {
      url: `/v1/me/billing/checkout`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createCheckoutRequest,
      signal,
    },
    options
  );
};

export const getCreateUserCheckoutSessionMutationOptions = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createUserCheckoutSession>>,
    TError,
    { data: CreateCheckoutRequest },
    TContext
  >;
  request?: SecondParameter<typeof instance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createUserCheckoutSession>>,
  TError,
  { data: CreateCheckoutRequest },
  TContext
> => {
  const mutationKey = ['createUserCheckoutSession'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createUserCheckoutSession>>,
    { data: CreateCheckoutRequest }
  > = (props) => {
    const { data } = props ?? {};

    return createUserCheckoutSession(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateUserCheckoutSessionMutationResult = NonNullable<
  Awaited<ReturnType<typeof createUserCheckoutSession>>
>;
export type CreateUserCheckoutSessionMutationBody = CreateCheckoutRequest;
export type CreateUserCheckoutSessionMutationError = ErrorType<ProblemDetails>;

/**
 * @summary Create checkout session
 */
export const useCreateUserCheckoutSession = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createUserCheckoutSession>>,
      TError,
      { data: CreateCheckoutRequest },
      TContext
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createUserCheckoutSession>>,
  TError,
  { data: CreateCheckoutRequest },
  TContext
> => {
  const mutationOptions = getCreateUserCheckoutSessionMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Creates a Stripe Billing Portal session for the authenticated user to manage subscriptions.
 * @summary Create billing portal session
 */
export const createUserPortalSession = (
  createPortalRequest: CreatePortalRequest,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<PortalResponse>(
    {
      url: `/v1/me/billing/portal`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createPortalRequest,
      signal,
    },
    options
  );
};

export const getCreateUserPortalSessionMutationOptions = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createUserPortalSession>>,
    TError,
    { data: CreatePortalRequest },
    TContext
  >;
  request?: SecondParameter<typeof instance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createUserPortalSession>>,
  TError,
  { data: CreatePortalRequest },
  TContext
> => {
  const mutationKey = ['createUserPortalSession'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createUserPortalSession>>,
    { data: CreatePortalRequest }
  > = (props) => {
    const { data } = props ?? {};

    return createUserPortalSession(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateUserPortalSessionMutationResult = NonNullable<
  Awaited<ReturnType<typeof createUserPortalSession>>
>;
export type CreateUserPortalSessionMutationBody = CreatePortalRequest;
export type CreateUserPortalSessionMutationError = ErrorType<ProblemDetails>;

/**
 * @summary Create billing portal session
 */
export const useCreateUserPortalSession = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createUserPortalSession>>,
      TError,
      { data: CreatePortalRequest },
      TContext
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createUserPortalSession>>,
  TError,
  { data: CreatePortalRequest },
  TContext
> => {
  const mutationOptions = getCreateUserPortalSessionMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Gets the subscription containing a line item for the specified product.
 * @summary Get subscription for product
 */
export const getUserProductSubscription = (
  productId: string,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<GetSubscriptionResponse>(
    { url: `/v1/me/billing/products/${productId}/subscription`, method: 'GET', signal },
    options
  );
};

export const getGetUserProductSubscriptionQueryKey = (productId?: string) => {
  return [`/v1/me/billing/products/${productId}/subscription`] as const;
};

export const getGetUserProductSubscriptionQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserProductSubscription>>,
  TError = ErrorType<ProblemDetails>,
>(
  productId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserProductSubscription>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserProductSubscriptionQueryKey(productId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserProductSubscription>>> = ({
    signal,
  }) => getUserProductSubscription(productId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!productId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserProductSubscription>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetUserProductSubscriptionQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserProductSubscription>>
>;
export type GetUserProductSubscriptionQueryError = ErrorType<ProblemDetails>;

export function useGetUserProductSubscription<
  TData = Awaited<ReturnType<typeof getUserProductSubscription>>,
  TError = ErrorType<ProblemDetails>,
>(
  productId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserProductSubscription>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserProductSubscription>>,
          TError,
          Awaited<ReturnType<typeof getUserProductSubscription>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetUserProductSubscription<
  TData = Awaited<ReturnType<typeof getUserProductSubscription>>,
  TError = ErrorType<ProblemDetails>,
>(
  productId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserProductSubscription>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserProductSubscription>>,
          TError,
          Awaited<ReturnType<typeof getUserProductSubscription>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetUserProductSubscription<
  TData = Awaited<ReturnType<typeof getUserProductSubscription>>,
  TError = ErrorType<ProblemDetails>,
>(
  productId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserProductSubscription>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get subscription for product
 */

export function useGetUserProductSubscription<
  TData = Awaited<ReturnType<typeof getUserProductSubscription>>,
  TError = ErrorType<ProblemDetails>,
>(
  productId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserProductSubscription>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetUserProductSubscriptionQueryOptions(productId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Gets add-ons available for purchase for the specified subscription.
 * @summary Get available add-ons for subscription
 */
export const getUserSubscriptionAvailableAddOns = (
  subscriptionId: string,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<AvailableAddOnsResponse>(
    {
      url: `/v1/me/billing/subscriptions/${subscriptionId}/available-addons`,
      method: 'GET',
      signal,
    },
    options
  );
};

export const getGetUserSubscriptionAvailableAddOnsQueryKey = (subscriptionId?: string) => {
  return [`/v1/me/billing/subscriptions/${subscriptionId}/available-addons`] as const;
};

export const getGetUserSubscriptionAvailableAddOnsQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>,
  TError = ErrorType<ProblemDetails>,
>(
  subscriptionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUserSubscriptionAvailableAddOnsQueryKey(subscriptionId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>> = ({
    signal,
  }) => getUserSubscriptionAvailableAddOns(subscriptionId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!subscriptionId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetUserSubscriptionAvailableAddOnsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>
>;
export type GetUserSubscriptionAvailableAddOnsQueryError = ErrorType<ProblemDetails>;

export function useGetUserSubscriptionAvailableAddOns<
  TData = Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>,
  TError = ErrorType<ProblemDetails>,
>(
  subscriptionId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>,
          TError,
          Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetUserSubscriptionAvailableAddOns<
  TData = Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>,
  TError = ErrorType<ProblemDetails>,
>(
  subscriptionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>,
          TError,
          Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetUserSubscriptionAvailableAddOns<
  TData = Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>,
  TError = ErrorType<ProblemDetails>,
>(
  subscriptionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get available add-ons for subscription
 */

export function useGetUserSubscriptionAvailableAddOns<
  TData = Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>,
  TError = ErrorType<ProblemDetails>,
>(
  subscriptionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserSubscriptionAvailableAddOns>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetUserSubscriptionAvailableAddOnsQueryOptions(subscriptionId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Previews price changes for proposed subscription modifications.
 * @summary Preview subscription changes
 */
export const previewUserSubscriptionChange = (
  subscriptionId: string,
  previewSubscriptionChangeRequest: PreviewSubscriptionChangeRequest,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<PreviewSubscriptionChangeResponse>(
    {
      url: `/v1/me/billing/subscriptions/${subscriptionId}/preview`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: previewSubscriptionChangeRequest,
      signal,
    },
    options
  );
};

export const getPreviewUserSubscriptionChangeMutationOptions = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof previewUserSubscriptionChange>>,
    TError,
    { subscriptionId: string; data: PreviewSubscriptionChangeRequest },
    TContext
  >;
  request?: SecondParameter<typeof instance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof previewUserSubscriptionChange>>,
  TError,
  { subscriptionId: string; data: PreviewSubscriptionChangeRequest },
  TContext
> => {
  const mutationKey = ['previewUserSubscriptionChange'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof previewUserSubscriptionChange>>,
    { subscriptionId: string; data: PreviewSubscriptionChangeRequest }
  > = (props) => {
    const { subscriptionId, data } = props ?? {};

    return previewUserSubscriptionChange(subscriptionId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PreviewUserSubscriptionChangeMutationResult = NonNullable<
  Awaited<ReturnType<typeof previewUserSubscriptionChange>>
>;
export type PreviewUserSubscriptionChangeMutationBody = PreviewSubscriptionChangeRequest;
export type PreviewUserSubscriptionChangeMutationError = ErrorType<ProblemDetails>;

/**
 * @summary Preview subscription changes
 */
export const usePreviewUserSubscriptionChange = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof previewUserSubscriptionChange>>,
      TError,
      { subscriptionId: string; data: PreviewSubscriptionChangeRequest },
      TContext
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof previewUserSubscriptionChange>>,
  TError,
  { subscriptionId: string; data: PreviewSubscriptionChangeRequest },
  TContext
> => {
  const mutationOptions = getPreviewUserSubscriptionChangeMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Applies subscription changes atomically. Upgrades (new items, quantity increases) are charged immediately. Downgrades (removed items, quantity decreases) are scheduled for the next billing period.
 * @summary Apply subscription changes
 */
export const applyUserSubscriptionChanges = (
  subscriptionId: string,
  previewSubscriptionChangeRequest: PreviewSubscriptionChangeRequest,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<GetSubscriptionResponse>(
    {
      url: `/v1/me/billing/subscriptions/${subscriptionId}/apply`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: previewSubscriptionChangeRequest,
      signal,
    },
    options
  );
};

export const getApplyUserSubscriptionChangesMutationOptions = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof applyUserSubscriptionChanges>>,
    TError,
    { subscriptionId: string; data: PreviewSubscriptionChangeRequest },
    TContext
  >;
  request?: SecondParameter<typeof instance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof applyUserSubscriptionChanges>>,
  TError,
  { subscriptionId: string; data: PreviewSubscriptionChangeRequest },
  TContext
> => {
  const mutationKey = ['applyUserSubscriptionChanges'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof applyUserSubscriptionChanges>>,
    { subscriptionId: string; data: PreviewSubscriptionChangeRequest }
  > = (props) => {
    const { subscriptionId, data } = props ?? {};

    return applyUserSubscriptionChanges(subscriptionId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ApplyUserSubscriptionChangesMutationResult = NonNullable<
  Awaited<ReturnType<typeof applyUserSubscriptionChanges>>
>;
export type ApplyUserSubscriptionChangesMutationBody = PreviewSubscriptionChangeRequest;
export type ApplyUserSubscriptionChangesMutationError = ErrorType<ProblemDetails>;

/**
 * @summary Apply subscription changes
 */
export const useApplyUserSubscriptionChanges = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof applyUserSubscriptionChanges>>,
      TError,
      { subscriptionId: string; data: PreviewSubscriptionChangeRequest },
      TContext
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof applyUserSubscriptionChanges>>,
  TError,
  { subscriptionId: string; data: PreviewSubscriptionChangeRequest },
  TContext
> => {
  const mutationOptions = getApplyUserSubscriptionChangesMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Lists the consultant hour packages a product currently offers for purchase (active packages with an active Stripe price), in display order, with hours, price amount (minor units) and currency. Returns an empty list when the product offers none. Requires Administrator or Billing role.
 * @summary List consultant hour packages
 */
export const getOrgConsultantHourPackages = (
  orgId: string,
  productId: string,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<ConsultantHourPackageOffersResponse>(
    {
      url: `/v1/org/${orgId}/billing/products/${productId}/consultant-hour-packages`,
      method: 'GET',
      signal,
    },
    options
  );
};

export const getGetOrgConsultantHourPackagesQueryKey = (orgId?: string, productId?: string) => {
  return [`/v1/org/${orgId}/billing/products/${productId}/consultant-hour-packages`] as const;
};

export const getGetOrgConsultantHourPackagesQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrgConsultantHourPackages>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgConsultantHourPackages>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOrgConsultantHourPackagesQueryKey(orgId, productId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrgConsultantHourPackages>>> = ({
    signal,
  }) => getOrgConsultantHourPackages(orgId, productId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!(orgId && productId), ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrgConsultantHourPackages>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetOrgConsultantHourPackagesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrgConsultantHourPackages>>
>;
export type GetOrgConsultantHourPackagesQueryError = ErrorType<ProblemDetails>;

export function useGetOrgConsultantHourPackages<
  TData = Awaited<ReturnType<typeof getOrgConsultantHourPackages>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgConsultantHourPackages>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrgConsultantHourPackages>>,
          TError,
          Awaited<ReturnType<typeof getOrgConsultantHourPackages>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrgConsultantHourPackages<
  TData = Awaited<ReturnType<typeof getOrgConsultantHourPackages>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgConsultantHourPackages>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrgConsultantHourPackages>>,
          TError,
          Awaited<ReturnType<typeof getOrgConsultantHourPackages>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrgConsultantHourPackages<
  TData = Awaited<ReturnType<typeof getOrgConsultantHourPackages>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgConsultantHourPackages>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List consultant hour packages
 */

export function useGetOrgConsultantHourPackages<
  TData = Awaited<ReturnType<typeof getOrgConsultantHourPackages>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrgConsultantHourPackages>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetOrgConsultantHourPackagesQueryOptions(orgId, productId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Creates a one-time Stripe Checkout session for the organization to purchase a consultant hour package. Redirect the user to the returned checkoutUrl. The package's hours are credited to the organization (consultantHoursAssigned/consultantHoursAvailable) when Stripe confirms payment, not when the session is created. The organization's billing country must be set and its currency must match the package price. Requires Administrator or Billing role.
 * @summary Create consultant hours checkout session
 */
export const createOrgConsultantHoursCheckoutSession = (
  orgId: string,
  createConsultantHoursCheckoutRequest: CreateConsultantHoursCheckoutRequest,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<CheckoutResponse>(
    {
      url: `/v1/org/${orgId}/billing/consultant-hours/checkout`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createConsultantHoursCheckoutRequest,
      signal,
    },
    options
  );
};

export const getCreateOrgConsultantHoursCheckoutSessionMutationOptions = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOrgConsultantHoursCheckoutSession>>,
    TError,
    { orgId: string; data: CreateConsultantHoursCheckoutRequest },
    TContext
  >;
  request?: SecondParameter<typeof instance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createOrgConsultantHoursCheckoutSession>>,
  TError,
  { orgId: string; data: CreateConsultantHoursCheckoutRequest },
  TContext
> => {
  const mutationKey = ['createOrgConsultantHoursCheckoutSession'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createOrgConsultantHoursCheckoutSession>>,
    { orgId: string; data: CreateConsultantHoursCheckoutRequest }
  > = (props) => {
    const { orgId, data } = props ?? {};

    return createOrgConsultantHoursCheckoutSession(orgId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateOrgConsultantHoursCheckoutSessionMutationResult = NonNullable<
  Awaited<ReturnType<typeof createOrgConsultantHoursCheckoutSession>>
>;
export type CreateOrgConsultantHoursCheckoutSessionMutationBody =
  CreateConsultantHoursCheckoutRequest;
export type CreateOrgConsultantHoursCheckoutSessionMutationError = ErrorType<ProblemDetails>;

/**
 * @summary Create consultant hours checkout session
 */
export const useCreateOrgConsultantHoursCheckoutSession = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createOrgConsultantHoursCheckoutSession>>,
      TError,
      { orgId: string; data: CreateConsultantHoursCheckoutRequest },
      TContext
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createOrgConsultantHoursCheckoutSession>>,
  TError,
  { orgId: string; data: CreateConsultantHoursCheckoutRequest },
  TContext
> => {
  const mutationOptions = getCreateOrgConsultantHoursCheckoutSessionMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
